import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import config from 'config'

import useSnackbar from '../Snackbar/useSnackbar'

// REL-F04 · Release-flag loader hook. Mirrors useLogistics but talks to the
// REL-11 endpoints: POST /release/loader/check (dry-run classification, no
// writes) and PATCH /release/loader/confirm (apply). The confirm re-runs the
// classifier server-side from the SAME raw grid rows — never the preview `rows`
// (they carry extra fields the schema rejects) — so `items` (the parsed grid) is
// kept in state and resent on confirm.
const INITIAL_STATE = {
  items: [],
  rows: [],
  summary: null,
  isUpdating: false,
  error: null,
}

const useReleaseLoader = () => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const [state, setState] = useState(INITIAL_STATE)

  const setStartUpdating = useCallback(() => {
    setState((s) => ({ ...s, isUpdating: true }))
  }, [setState])

  const setStopUpdating = useCallback(
    (data) => {
      setState((s) => ({
        ...s,
        isUpdating: false,
        ...data,
        error: data?.error || null,
      }))
    },
    [setState]
  )

  const handleError = useCallback(
    (e) => {
      const message =
        e.response?.data?.message ||
        t('common.error.fetchData', {
          attribute: e.response?.status,
        })

      setStopUpdating({ error: e })

      showSnackbarMessage({
        severity: 'error',
        message,
      })
    },
    [setStopUpdating, showSnackbarMessage, t]
  )

  const checkReleaseRows = useCallback(
    async (items, callback) => {
      setStartUpdating()

      try {
        const result = await axios.post(
          `${config.apiBaseUrl}release/loader/check`,
          { items }
        )

        const body = result.data.body

        setStopUpdating({
          items,
          rows: body.rows || [],
          summary: body.summary || null,
        })
        callback && callback()
      } catch (e) {
        handleError(e)
      }
    },
    [setStartUpdating, setStopUpdating, handleError]
  )

  const confirmReleaseRows = useCallback(
    async (items, callback) => {
      setStartUpdating()

      try {
        const result = await axios.patch(
          `${config.apiBaseUrl}release/loader/confirm`,
          { items }
        )

        const applied = result.data?.body?.applied || 0

        setStopUpdating({ ...INITIAL_STATE })

        showSnackbarMessage({
          severity: 'success',
          message: t('dataLoad.release.importSuccess', { count: applied }),
        })
        callback && callback()
      } catch (e) {
        handleError(e)
      }
    },
    [setStartUpdating, setStopUpdating, handleError, showSnackbarMessage, t]
  )

  return {
    items: state.items,
    rows: state.rows,
    summary: state.summary,
    isUpdating: state.isUpdating,
    error: state.error,
    checkReleaseRows,
    confirmReleaseRows,
  }
}

export default useReleaseLoader
